/* eslint-disable indent */
'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Phone, User, MessageSquare, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { event as gaEvent } from '@/lib/gtag'; // adjust path if needed
import { submitGeneralFormToPixxiCRM } from '@/app/utils/pixxi-api';

const AgentContactModal = ({ isOpen, onClose, agent }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [feedback, setFeedback] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const submitData = {
      name: formData.name,
      phone: formData.phone,
      email: formData.email,

      extraData: {
        title: `Website Lead - Contact ${agent?.name || 'Agent'}`,
        source: 'Website Lead form for contact agent',
        message: formData?.message || '',
      },
    };

    try {
      // Simulate a delay for demonstration purposes
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const result = await submitGeneralFormToPixxiCRM(submitData);

      if (result.success) {
        setFeedback('Your message has been submitted successfully!');

        gaEvent({
          action: 'submit_form',
          category: 'Contact',
          label: `Agent: ${agent?.name || 'Agent'}`,
        });

        // Simulate API call
        setTimeout(() => {
          setIsSubmitting(false);
          setIsSuccess(true);

          // Simulate API call
          setTimeout(() => {
            setIsSubmitting(false);
            setIsSuccess(true);

            // Reset after showing success message
            setTimeout(() => {
              setIsSuccess(false);
              setFormData({
                name: '',
                email: '',
                mobile: '',
                message: '',
              });
              onClose();
            }, 8000);
          }, 1500);
        }, 1500);
      } else {
        setSubmitStatus('error');
        setSubmitMessage(
          result.message || 'Failed to submit form. Please try again.'
        );
      }
    } catch (error) {
      setFeedback(
        'Error: There was a problem with your submission. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }

    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);

      // Reset after showing success message
      setTimeout(() => {
        setIsSuccess(false);
        setFormData({
          name: '',
          email: '',
          mobile: '',
          message: '',
        });
        onClose();
      }, 8000);
    }, 1500);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='sm:max-w-[500px] p-0 overflow-hidden bg-white dark:bg-gray-900'>
        <div className='bg-gradient-to-r from-primarycolor to-secondarycolor p-6 text-white'>
          <DialogTitle className='text-2xl font-bold mb-2'>
            {isSuccess ? 'Thank You!' : `Contact ${agent?.name || 'Agent'}`}
          </DialogTitle>
          <DialogDescription className='text-black max-w-lg font-bold dark:text-black text-lg'>
            {isSuccess
              ? `We've received your message and ${
                  agent?.name || 'our agent'
                } will get back to you shortly.`
              : `Fill out the form below to get in touch with ${
                  agent?.name || 'our agent'
                }.`}
          </DialogDescription>
        </div>

        <AnimatePresence mode='wait'>
          {isSuccess ? (
            <motion.div
              key='success'
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className='p-6 flex flex-col items-center justify-center'
            >
              <div className='w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mb-4'>
                <Check className='w-8 h-8 text-green-600 dark:text-green-400' />
              </div>
              <h3 className='text-xl font-semibold text-gray-900 dark:text-white mb-2'>
                Message Sent Successfully!
              </h3>
              <p className='text-gray-600 dark:text-gray-400 text-center mb-6'>
                Thank you for reaching out to {agent?.name || 'our agent'}. Your
                inquiry has been received and you can expect a response within
                24 hours.
              </p>
              <button
                onClick={onClose}
                className='px-6 py-2 bg-primarycolor text-black hover:bg-secondarycolor hover:text-black transition-colors rounded-md'
              >
                Close
              </button>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className='p-6 space-y-4'>
              <div className='space-y-2'>
                <label
                  htmlFor='name'
                  className='block text-sm font-medium text-gray-700 dark:text-gray-300'
                >
                  Full Name
                </label>
                <div className='flex items-center border rounded-md focus-within:ring-1 focus-within:ring-primarycolor'>
                  <User className='ml-3 h-4 w-4 text-gray-400' />
                  <input
                    id='name'
                    name='name'
                    value={formData.name}
                    onChange={handleChange}
                    placeholder='John Doe'
                    required
                    maxLength={25} // <-- Set your desired limit here
                    className='w-full px-3 py-2 text-[16px] border-0 focus:outline-none focus:ring-0 bg-transparent'
                  />
                </div>
              </div>

              <div className='space-y-2'>
                <label
                  htmlFor='email'
                  className='block text-sm font-medium text-gray-700 dark:text-gray-300'
                >
                  Email Address
                </label>
                <div className='flex items-center border rounded-md focus-within:ring-1 focus-within:ring-primarycolor'>
                  <Mail className='ml-3 h-4 w-4 text-gray-400' />
                  <input
                    id='email'
                    name='email'
                    type='email'
                    value={formData.email}
                    onChange={handleChange}
                    placeholder='john.doe@example.com'
                    required
                    maxLength={25} // <-- Set your desired limit here
                    className='w-full px-3 py-2 text-[16px] border-0 focus:outline-none focus:ring-0 bg-transparent'
                  />
                </div>
              </div>

              <div className='space-y-2'>
                <label
                  htmlFor='mobile'
                  className='block text-sm font-medium text-gray-700 dark:text-gray-300'
                >
                  Mobile Number
                </label>
                <div className='flex items-center border rounded-md focus-within:ring-1 focus-within:ring-primarycolor'>
                  <Phone className='ml-3 h-4 w-4 text-gray-400' />
                  <input
                    id='mobile'
                    name='mobile'
                    type='tel'
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder='+971 50 123 4567'
                    required
                    maxLength={10} // <-- Set your desired limit here
                    className='w-full px-3 py-2 text-[16px] border-0 focus:outline-none focus:ring-0 bg-transparent'
                  />
                </div>
              </div>

              <div className='space-y-2'>
                <label
                  htmlFor='message'
                  className='block text-sm font-medium text-gray-700 dark:text-gray-300'
                >
                  Message
                </label>
                <div className='flex items-start border rounded-md focus-within:ring-1 focus-within:ring-primarycolor'>
                  <MessageSquare className='ml-3 mt-3 h-4 w-4 text-gray-400 flex-shrink-0' />
                  <textarea
                    id='message'
                    name='message'
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={`I'm interested in working with ${
                      agent?.name || 'you'
                    } to find my dream property...`}
                    rows={4}
                    maxLength={500} // <-- Set your desired limit here
                    className='w-full px-3 py-2 text-[16px] border-0 focus:outline-none focus:ring-0 bg-transparent resize-none'
                    required
                  />
                </div>
              </div>

              <DialogFooter className='flex flex-col sm:flex-row sm:justify-between gap-2 pt-4'>
                <button
                  type='button'
                  onClick={onClose}
                  className='px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors rounded-md'
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  disabled={isSubmitting}
                  className='px-6 py-2 bg-primarycolor text-black hover:bg-secondarycolor hover:text-black transition-colors rounded-md'
                >
                  {isSubmitting ? (
                    <>
                      <svg
                        className='animate-spin -ml-1 mr-2 h-4 w-4 text-black inline-block'
                        xmlns='http://www.w3.org/2000/svg'
                        fill='none'
                        viewBox='0 0 24 24'
                      >
                        <circle
                          className='opacity-25'
                          cx='12'
                          cy='12'
                          r='10'
                          stroke='currentColor'
                          strokeWidth='4'
                        ></circle>
                        <path
                          className='opacity-75'
                          fill='currentColor'
                          d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                        ></path>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </DialogFooter>
            </form>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

export default AgentContactModal;
